import { userServices } from '@/services'
import { Layout } from '@/shared/components/layout'
import { Navigate, Outlet, useNavigate } from 'react-router-dom'
import { usePermify } from '@permify/react-role'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { useRoleSettings } from '@/hooks/use-role-settings'
import { Button, Checkbox, Dialog, DialogBody, DialogFooter, DialogHeader, Typography } from '@material-tailwind/react'
import { DocumentNumberInput } from '@/components/document-number-input'
import { isAValidRut } from '@/utils'
import { useEffect, useState } from 'react'
import { useUpdateCurrentUserMutation } from '@/store/services'
import { fetchCurrentUser } from '@/store/slices/userSlice'
import PropTypes from 'prop-types'

const FORCE_IDENTIFIER_SETTING_KEY = 'require_identifier_completion'

const getRoleNames = (user = {}) => {
  const fromRoles = Array.isArray(user?.roles)
    ? user.roles.map((role) => String(role?.name || '').toLowerCase())
    : []
  const fromRoleNames = Array.isArray(user?.roleNames)
    ? user.roleNames.map((name) => String(name || '').toLowerCase())
    : []

  return [...fromRoles, ...fromRoleNames]
}

const getRoleSpecificCorrectionMessage = (user = {}) => {
  const roleNames = getRoleNames(user)

  if (roleNames.includes('contractor')) {
    return 'Si el nombre registrado no corresponde al usuario actual, debes solicitar la corrección a la empresa mandante.'
  }

  if (roleNames.includes('admin') || roleNames.includes('administrador mandante')) {
    return 'Si el nombre registrado no corresponde al usuario actual, debes gestionarlo directamente con APS.'
  }

  return null
}

const getUserDisplayName = (user = {}) => {
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim()
  return fullName || user?.email || 'usuario'
}

const RequiredIdentifierModal = ({ open, user, onSubmit, isSaving, onLogout }) => {
  const [documentNumber, setDocumentNumber] = useState(user?.documentNumber || '')
  const [hasLocalDocumentNumber, setHasLocalDocumentNumber] = useState(user?.hasLocalDocumentNumber !== false)
  const [errorMessage, setErrorMessage] = useState('')
  const roleSpecificCorrectionMessage = getRoleSpecificCorrectionMessage(user)

  useEffect(() => {
    setDocumentNumber(user?.documentNumber || '')
    setHasLocalDocumentNumber(user?.hasLocalDocumentNumber !== false)
    setErrorMessage('')
  }, [user?.id, user?.documentNumber, user?.hasLocalDocumentNumber])

  const handleSubmit = async () => {
    const value = String(documentNumber || '').trim()

    if (!value) {
      setErrorMessage('Debes ingresar un RUT o identificador para continuar.')
      return
    }

    if (hasLocalDocumentNumber && !isAValidRut(value)) {
      setErrorMessage('El RUT no es válido.')
      return
    }

    if (!hasLocalDocumentNumber && !/^[0-9A-Z]+$/i.test(value)) {
      setErrorMessage('Número de documento inválido')
      return
    }

    setErrorMessage('')
    await onSubmit({
      document_number: value,
      has_local_document_number: hasLocalDocumentNumber
    })
  }

  return (
    <Dialog
      open={open}
      size='md'
      handler={() => {}}
      dismiss={{ enabled: false, outsidePress: false, escapeKey: false }}
    >
      <DialogHeader>Completar identificador obligatorio</DialogHeader>

      <DialogBody className='flex flex-col gap-4'>
        <Typography variant='paragraph' className='text-blue-gray-800'>
          Hola <strong>{getUserDisplayName(user)}</strong>. Para normalizar el servicio, debes registrar tu RUT o un identificador válido para continuar.
        </Typography>

        {roleSpecificCorrectionMessage && (
          <Typography variant='small' className='text-blue-gray-700'>
            {roleSpecificCorrectionMessage}
          </Typography>
        )}

        <div>
          <DocumentNumberInput
            required
            name='document_number'
            label={hasLocalDocumentNumber ? 'RUT' : 'DNI o pasaporte'}
            value={documentNumber}
            onChange={(event) => setDocumentNumber(event.target.value)}
            formatAsRut={hasLocalDocumentNumber}
            error={Boolean(errorMessage)}
            className='mb-4'
          />

          <Checkbox
            checked={!hasLocalDocumentNumber}
            onChange={(event) => {
              setHasLocalDocumentNumber(!event.target.checked)
              setDocumentNumber('')
              setErrorMessage('')
            }}
            ripple={false}
            className='hover:before:opacity-0'
            containerProps={{ className: 'p-0' }}
            labelProps={{ className: 'ml-1' }}
            label='No tiene RUT'
          />

          {errorMessage && (
            <Typography variant='small' className='text-red-500 mt-2'>
              {errorMessage}
            </Typography>
          )}
        </div>
      </DialogBody>

      <DialogFooter className='justify-between gap-3'>
        <Button variant='outlined' color='red' onClick={onLogout}>
          Cerrar sesión
        </Button>
        <Button onClick={handleSubmit} disabled={isSaving}>
          {isSaving ? 'Guardando...' : 'Guardar y continuar'}
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

RequiredIdentifierModal.propTypes = {
  open: PropTypes.bool.isRequired,
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    documentNumber: PropTypes.string,
    hasLocalDocumentNumber: PropTypes.bool,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSaving: PropTypes.bool.isRequired,
  onLogout: PropTypes.func.isRequired
}

export const Authenticated = () => {
  console.log('Authenticated rendered')
  const { setUser } = usePermify()
  const { current: user } = useSelector((state) => state.user)
  const dispatch = useDispatch()
  const { loading: roleSettingLoading, hasSetting } = useRoleSettings(FORCE_IDENTIFIER_SETTING_KEY)
  const [updateCurrentUser, { isLoading: isSavingIdentifier }] = useUpdateCurrentUserMutation()

  const navigate = useNavigate()

  const handleLogout = async () => {
    try {
      await userServices.signOut()
      navigate('/sign_in')
    } catch (e) {
      /* handle error */
    }
  }

  const [currentRole = {}] = user?.roles || []
  const permissions = currentRole?.permissionsArray || []
  const requiresIdentifier = hasSetting(FORCE_IDENTIFIER_SETTING_KEY)
  const hasIdentifier = String(user?.documentNumber || '').trim().length > 0
  const mustCompleteIdentifier = !roleSettingLoading && requiresIdentifier && !hasIdentifier

  if (!user?.id) return <Navigate to='/sign_in' />

  setUser({
    id: user?.info?.id,
    roles: [currentRole],
    permissions
  })

  return (
    <Layout handleLogout={handleLogout} >
      <RequiredIdentifierModal
        open={mustCompleteIdentifier}
        user={user}
        isSaving={isSavingIdentifier}
        onLogout={handleLogout}
        onSubmit={async (payload) => {
          try {
            await updateCurrentUser(payload).unwrap()
            await dispatch(fetchCurrentUser()).unwrap()
          } catch (error) {
            const firstError = error?.data?.errors?.[0]
            toast.error(firstError || 'No se pudo guardar el identificador.')
          }
        }}
      />
      < Outlet />
    </Layout >
  )
}

export const LayoutWithError = () => {
  console.log('LayoutWithError rendered')
  const { current: user } = useSelector((state) => state.user)
  const { t } = useTranslation()

  if (!user?.id) {
    toast.error(t('alert.user.unauthorized'))
    return <Navigate to='/sign_in' />
  }

  return (
    <Layout>
      <div>Error</div>
    </Layout>
  )
}
